import { AxiosResponse } from 'axios';
import { useAppMutation } from './useAppMutation';

interface CheckoutEnrolmentsBody {
  courseIds: string[];
  employeeIds: string[];
}

/**
 * The app's view of the server's checkout response body. Exported so the tests build their fixtures
 * through this type instead of hand-writing the shape in each one — that keeps a fixture from
 * drifting away from what the app actually reads.
 *
 * It does NOT verify the server: this interface is hand-written and has no link to the C#
 * CheckoutCourseResponse, so a renamed field there would leave everything here green. The server
 * side of that contract is pinned by CheckoutResponseWireContractTests.
 */
export interface CheckoutResponse {
  checkoutUrl: string;
}

/**
 * "Purchase separately / Add to cart" for the Enrol Students dialog: the server builds a Shopify
 * checkout for the selected courses × students and returns the hosted checkout URL; the selected
 * students are enrolled automatically after payment (webhook-driven, so not instant).
 */
export const useCheckoutEnrolments = () => {
  const { mutate, isPending } = useAppMutation<
    AxiosResponse<CheckoutResponse>,
    Error,
    CheckoutEnrolmentsBody
  >('post', '/TrainingManager/courses/checkout');

  const onCheckoutEnrolments = (
    body: CheckoutEnrolmentsBody,
    options?: {
      onSuccess?: (data: AxiosResponse<CheckoutResponse>) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    mutate(body, options);
  };

  return { onCheckoutEnrolments, isPending };
};
